// Base class for individual Three.js scenes mounted by the scene-router
// (see public/controllers/wellcode_scene_controller.js).
//
// Each module owns its own scene and camera. The router calls:
//   await module.mount(ctx)   when the host element enters the active band
//   module.update(t, scroll)  every animation frame while active
//   module.unmount()          when the host leaves the active band
//
// The router supplies the renderer + viewport size + texture loader via `ctx`.
// Modules MUST NOT call renderer.render() themselves; the router drives that.

export class SceneModule {
  constructor(hostElement) {
    this.host = hostElement
    this.scene = null
    this.camera = null
    this.ready = false
    this.disposables = []
  }

  // Subclasses override. Receives:
  //   ctx.THREE          — the dynamically imported THREE namespace
  //   ctx.renderer       — the shared WebGLRenderer
  //   ctx.size           — { width, height }
  //   ctx.textureLoader  — TextureLoader instance with WebP/AVIF support
  async mount(ctx) {  // eslint-disable-line no-unused-vars
    throw new Error("SceneModule subclass must implement mount(ctx)")
  }

  // Called every frame while the module is active. Subclasses override.
  // - deltaSeconds   : seconds since last frame
  // - scrollProgress : 0..1 progress through the module's host element
  update(deltaSeconds, scrollProgress) {  // eslint-disable-line no-unused-vars
    // No-op by default.
  }

  // Called when the camera/viewport changes size.
  resize(width, height) {  // eslint-disable-line no-unused-vars
    if (this.camera && this.camera.isPerspectiveCamera) {
      this.camera.aspect = width / height
      this.camera.updateProjectionMatrix()
    }
  }

  // Subclasses can override to release listener bindings. Default disposes
  // every geometry/material/texture registered via `register()`.
  unmount() {
    this.disposables.forEach(d => {
      if (d.geometry) d.geometry.dispose()
      if (d.material) {
        if (Array.isArray(d.material)) d.material.forEach(m => m.dispose())
        else                            d.material.dispose()
      }
      if (d.dispose) d.dispose()
    })
    this.disposables = []
    this.scene = null
    this.camera = null
    this.ready = false
  }

  register(...objects) {
    objects.forEach(o => this.disposables.push(o))
  }
}
